import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PageCard } from '../layout/page/PageCard';
import { FlagIcon } from '@heroicons/react/24/outline';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { DuplicateIconButton } from '../ui/DuplicateIconButton';
import { EditIconButton } from '../ui/EditIconButton';
import { IconActionButton } from '../ui/IconActionButton';
import { InlineAlert } from '../ui/InlineAlert';
import {
  createPlanningObjective,
  deletePlanningObjective,
  duplicatePlanningObjective,
  getObjectiveDeleteImpact,
  listPlanningObjectives,
  listPlanningWorkItems,
  PlanningCycle,
  PlanningDeleteImpact,
  PlanningObjective,
  updatePlanningObjective,
} from '../../services/strategicPlanningApi';
import { sortPlanningObjectives } from '../../utils/planningListSort';
import { nextSortOrder } from '../../utils/sortOrderList';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { formatPlanningObjectiveKind } from '../../types/PlanningObjectiveKind';
import { PlanningDuplicateCascadeOptions } from '../../types/PlanningDuplicateScope';
import { StrategicPlanningTabId } from '../../pages/strategic-planning/StrategicPlanningTabId';
import { PlanningDeleteConfirmModal } from './PlanningDeleteConfirmModal';
import { PlanningDuplicateModal } from './PlanningDuplicateModal';
import { PlanningFiltersBar } from './PlanningFiltersBar';
import { PlanningObjectiveFormModal } from './PlanningObjectiveFormModal';
import { PlanningStatusBadge } from './PlanningStatusBadge';

type PlanningObjectivesListViewProps = {
  canWrite: boolean;
  cycles: PlanningCycle[];
  selectedCycleFilter: string;
  selectedObjectiveKindFilter: string;
  createModalOpen: boolean;
  onCreateModalOpenChange: (open: boolean) => void;
  onCycleFilterChange: (value: string) => void;
  onObjectiveKindFilterChange: (value: string) => void;
  onNavigateToTab: (
    tab: StrategicPlanningTabId,
    filters?: { cycleId?: string; objectiveId?: string },
  ) => void;
};

export function PlanningObjectivesListView({
  canWrite,
  cycles,
  selectedCycleFilter,
  selectedObjectiveKindFilter,
  createModalOpen,
  onCreateModalOpenChange,
  onCycleFilterChange,
  onObjectiveKindFilterChange,
  onNavigateToTab,
}: PlanningObjectivesListViewProps) {
  const [objectives, setObjectives] = useState<PlanningObjective[]>([]);
  const [loading, setLoading] = useState(true);
  const [objectiveModalOpen, setObjectiveModalOpen] = useState(false);
  const [editingObjective, setEditingObjective] = useState<PlanningObjective | null>(null);
  const [deleteContext, setDeleteContext] = useState<{
    item: PlanningObjective;
    impact: PlanningDeleteImpact;
    transferOptions: Array<{ id: string; label: string }>;
  } | null>(null);
  const [duplicateContext, setDuplicateContext] = useState<{
    item: PlanningObjective;
    counts: { metas: number; marcos: number; tasks: number };
  } | null>(null);

  const filteredObjectives = useMemo(() => {
    let result = objectives;

    if (selectedCycleFilter) {
      result = result.filter((objective) => objective.cycleId === selectedCycleFilter);
    }

    if (selectedObjectiveKindFilter) {
      result = result.filter((objective) => objective.kind === selectedObjectiveKindFilter);
    }

    return sortPlanningObjectives(result, cycles);
  }, [cycles, objectives, selectedCycleFilter, selectedObjectiveKindFilter]);

  const loadObjectives = useCallback(async (): Promise<void> => {
    // Uma única chamada tenant-wide; ciclo/tipo filtrados no cliente.
    setObjectives(await listPlanningObjectives());
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      await loadObjectives();
    } catch {
      toast.error('Falha ao carregar objetivos.');
    } finally {
      setLoading(false);
    }
  }, [loadObjectives]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (createModalOpen) {
      setEditingObjective(null);
      setObjectiveModalOpen(true);
      onCreateModalOpenChange(false);
    }
  }, [createModalOpen, onCreateModalOpenChange]);

  async function handleObjectiveSubmit(
    cycleId: string,
    payload: Parameters<typeof updatePlanningObjective>[1],
  ): Promise<void> {
    try {
      if (editingObjective) {
        await updatePlanningObjective(editingObjective.id, payload);
        toast.success('Objetivo atualizado.');
      } else {
        await createPlanningObjective(cycleId, payload);
        toast.success('Objetivo criado.');
      }
      setEditingObjective(null);
      await refresh();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar objetivo.'));
      throw error;
    }
  }

  async function openObjectiveDelete(objective: PlanningObjective): Promise<void> {
    try {
      const impact = await getObjectiveDeleteImpact(objective.id);
      const transferOptions = objectives
        .filter(
          (item) =>
            item.id !== objective.id &&
            item.cycleId === objective.cycleId &&
            item.kind === objective.kind,
        )
        .map((item) => ({ id: item.id, label: item.title }));

      setDeleteContext({ item: objective, impact, transferOptions });
    } catch {
      toast.error('Não foi possível avaliar o impacto da exclusão.');
    }
  }

  async function openObjectiveDuplicate(objective: PlanningObjective): Promise<void> {
    try {
      const items = await listPlanningWorkItems({ objectiveId: objective.id });
      const metas = items.filter((item) => item.kind === 'Meta' && !item.parentWorkItemId);
      const marcos = items.filter((item) => item.kind === 'Marco');
      const tasks = items.filter((item) => item.kind === 'Tarefa');

      setDuplicateContext({
        item: objective,
        counts: { metas: metas.length, marcos: marcos.length, tasks: tasks.length },
      });
    } catch {
      toast.error('Não foi possível preparar a duplicação.');
    }
  }

  async function handleDeleteConfirm(transferChildrenToId?: string): Promise<void> {
    if (!deleteContext) return;

    try {
      await deletePlanningObjective(deleteContext.item.id, { transferChildrenToId });
      toast.success('Objetivo excluído.');
      setDeleteContext(null);
      await refresh();
    } catch {
      toast.error('Falha ao excluir.');
      throw new Error('delete_failed');
    }
  }

  async function handleDuplicateConfirm(options: PlanningDuplicateCascadeOptions): Promise<void> {
    if (!duplicateContext) return;

    await duplicatePlanningObjective(duplicateContext.item.id, {
      includeMetas: options.includeMetas,
      includeMarcos: options.includeMarcos,
      includeTasks: options.includeTasks,
    });
    toast.success('Objetivo duplicado.');
    setDuplicateContext(null);
    await refresh();
  }

  return (
    <div className="space-y-4">
      <PlanningFiltersBar
        cycles={cycles}
        selectedCycleFilter={selectedCycleFilter}
        onCycleFilterChange={onCycleFilterChange}
        selectedObjectiveKindFilter={selectedObjectiveKindFilter}
        onObjectiveKindFilterChange={onObjectiveKindFilterChange}
        showObjectiveKindFilter
        counterLabel={`${filteredObjectives.length} objetivo(s)`}
      />

      <PageCard title="Objetivos">
        {loading ? (
          <p className="text-sm text-secondary-600">Carregando…</p>
        ) : filteredObjectives.length === 0 ? (
          <InlineAlert>Nenhum objetivo cadastrado. Selecione um ciclo e cadastre o primeiro objetivo.</InlineAlert>
        ) : (
          <div className="overflow-x-auto">
            <table className="erp-table w-full min-w-[720px]">
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th className="w-48">{canWrite ? 'Ações' : ''}</th>
                </tr>
              </thead>
              <tbody>
                {filteredObjectives.map((objective) => (
                    <tr key={objective.id}>
                      <td>
                        <div className="font-medium text-secondary-900">{objective.title}</div>
                        {objective.description ? (
                          <div
                            className="max-w-xs truncate text-xs text-secondary-500"
                            title={objective.description}
                          >
                            {objective.description}
                          </div>
                        ) : null}
                      </td>
                      <td>{formatPlanningObjectiveKind(objective.kind)}</td>
                      <td>
                        <PlanningStatusBadge scope="objective" status={objective.status} />
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <IconActionButton
                            label="Ver metas"
                            icon={FlagIcon}
                            onClick={() =>
                              onNavigateToTab('metas', {
                                cycleId: objective.cycleId,
                                objectiveId: objective.id,
                              })
                            }
                          />
                          {canWrite ? (
                            <>
                              <DuplicateIconButton
                                label="Duplicar objetivo"
                                onClick={() => void openObjectiveDuplicate(objective)}
                              />
                              <EditIconButton
                                label="Editar objetivo"
                                onClick={() => {
                                  setEditingObjective(objective);
                                  setObjectiveModalOpen(true);
                                }}
                              />
                              <DeleteIconButton
                                label="Excluir objetivo"
                                onClick={() => void openObjectiveDelete(objective)}
                              />
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>

      <PlanningObjectiveFormModal
        open={objectiveModalOpen}
        cycles={cycles}
        initial={editingObjective}
        defaultCycleId={selectedCycleFilter}
        defaultKind={selectedObjectiveKindFilter}
        defaultSortOrder={nextSortOrder(
          objectives.filter(
            (objective) =>
              objective.cycleId ===
              (editingObjective?.cycleId ?? selectedCycleFilter ?? cycles[0]?.id),
          ),
        )}
        onClose={() => {
          setObjectiveModalOpen(false);
          setEditingObjective(null);
        }}
        onSubmit={handleObjectiveSubmit}
      />

      {deleteContext ? (
        <PlanningDeleteConfirmModal
          open
          title="Excluir objetivo"
          itemLabel={deleteContext.item.title}
          impact={deleteContext.impact}
          transferOptions={deleteContext.transferOptions}
          transferLabel="Mover itens para o objetivo"
          onClose={() => setDeleteContext(null)}
          onConfirm={handleDeleteConfirm}
        />
      ) : null}

      {duplicateContext ? (
        <PlanningDuplicateModal
          open
          entityType="objective"
          itemLabel={duplicateContext.item.title}
          counts={duplicateContext.counts}
          onClose={() => setDuplicateContext(null)}
          onConfirm={handleDuplicateConfirm}
        />
      ) : null}
    </div>
  );
}
